import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ComingSoonBadge } from '@/components/common/ComingSoonBadge';
import { billingReviewPath } from '@/lib/constants/routes';
import { formatCents } from '@/lib/utils/format.utils';
import type { BillingInterval, Plan } from '@/lib/types/billing';
import { BILLING_STRINGS } from '../constants/billing-strings';
import { formatAccessTerm } from '../access-term';
import { PlanBenefits } from './PlanBenefits';

const COPY = BILLING_STRINGS.PURCHASE;

/**
 * How each interval renders its price suffix, resolved once per plan.
 *
 * A `one_time` plan is not "per" anything - it buys a fixed access term, which
 * is why its term is stated separately below (A-78).
 */
export const INTERVAL_SUFFIX: Record<BillingInterval, string> = {
  monthly: COPY.PER_MONTH,
  annual: COPY.PER_YEAR,
  one_time: ` ${COPY.ONE_TIME}`,
};

export interface PlanSummaryCardProps {
  plan: Plan;
  /** True when this is the plan the startup already holds. */
  isCurrent: boolean;
  /** Blocks selection, e.g. while a subscription is live. */
  disabled?: boolean;
  /** The only plan on sale: centred and roomier, as in the design. */
  featured?: boolean;
  /** The Issue Report is not on sale (FR-203): a non-clickable "Coming Soon" replaces the buy action. */
  comingSoon?: boolean;
  /**
   * The Issue Report offer is still loading or failed to load: neither a buy
   * action nor "Coming Soon" is shown, so a buy button never flashes and turns
   * into "Coming Soon" (FR-203). The rest of the card stays.
   */
  offerUnknown?: boolean;
}

/**
 * One plan in the catalogue (FR-39, A-76).
 *
 * Deliberately a card in a list rather than a column in a comparison table: the
 * catalogue is admin-managed and its size is not known at build time, so a
 * layout that assumed two plans would break the first time an admin added a
 * third.
 *
 * It carries everything needed to COMPARE plans and nothing needed to BUY one:
 * the promo code is entered on the review step, beside the total it changes
 * (Sprint 7 QA). No data-room seat is ever sold (FR-138), so no seat price is
 * shown.
 *
 * "Due Diligence platform access" is MYN-111.5's wording. The Figma still says
 * "investor tracking"; the ticket wins and the old phrase must not come back.
 */
/** The line under the price, per interval, resolved once. */
function billedLine(plan: Plan): string {
  if (plan.billingInterval === 'monthly') return COPY.BILLED_MONTHLY;
  if (plan.billingInterval === 'annual') return COPY.BILLED_ANNUALLY;
  return COPY.BILLED_ONCE.replace('{term}', formatAccessTerm(plan) ?? '');
}

export function PlanSummaryCard({
  plan,
  isCurrent,
  disabled = false,
  featured = false,
  comingSoon = false,
  offerUnknown = false,
}: PlanSummaryCardProps) {
  const locked = isCurrent || disabled;

  return (
    <article
      className={`rounded-card flex flex-col bg-white shadow-card ${featured ? 'p-10 text-center' : 'border-border-strong border p-6'}`}
    >
      <header className={`flex flex-col gap-2 ${featured ? 'items-center' : ''}`}>
        <div className="flex items-center gap-2">
          <p className="text-green text-xs font-semibold tracking-[0.08em] uppercase">{COPY.PLAN_EYEBROW}</p>
          {isCurrent && <Badge variant="green-soft" size="sm">{COPY.CURRENT_PLAN_BADGE}</Badge>}
        </div>
        <h3 className={`text-text-primary font-semibold ${featured ? 'text-3xl' : 'text-xl'}`}>{plan.name}</h3>
      </header>

      <div className="mt-6">
        <p className="text-text-primary">
          <span className={`font-bold ${featured ? 'text-5xl' : 'text-4xl'}`}>{formatCents(plan.priceCents)}</span>
          <span className="text-text-secondary ml-1 text-base">{INTERVAL_SUFFIX[plan.billingInterval]}</span>
        </p>
        <p className="text-text-muted mt-2 text-sm">{billedLine(plan)}</p>
      </div>

      <div className="border-border-subtle my-6 border-t" />

      <div className={`flex-1 ${featured ? 'text-left' : ''}`}>
        <PlanBenefits includedSeats={plan.seatLimit} columns={featured ? 2 : 1} />
      </div>

      {/* A link to the review step, so the chosen plan lives in the URL. A
          locked card renders a disabled button instead: a link cannot be
          disabled, and an enabled one would let a live subscriber reach a
          review step that can only refuse them. */}
      {offerUnknown ? null : comingSoon ? (
        <div className="mt-8 flex justify-center">
          <ComingSoonBadge />
        </div>
      ) : locked ? (
        <Button className="mt-8 w-full" disabled>
          {COPY.SELECT}
        </Button>
      ) : (
        <Button asChild className="mt-8 inline-flex w-full items-center justify-center gap-2">
          <Link to={billingReviewPath(plan.id)}>
            {COPY.SELECT}
            <ArrowRight aria-hidden="true" className="h-4 w-4" />
          </Link>
        </Button>
      )}
    </article>
  );
}
